import { memo, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { getTranslationKeyError } from "../utils/translationValidation";

function AddPageDialog({ open, onClose, onCreate, initialPlatform = "web" }) {
  const [submitting, setSubmitting] = useState(false);
  const [pageName, setPageName] = useState("");
  const [pageDesc, setPageDesc] = useState("");
  const [platform, setPlatform] = useState(initialPlatform);

  const [texts, setTexts] = useState([]);
  const [draftKey, setDraftKey] = useState("");

  const keyExists = useMemo(() => texts.includes(draftKey), [texts, draftKey]);
  const draftKeyError = getTranslationKeyError(draftKey) || (keyExists ? "Déjà ajouté" : "");

  const canCreate = useMemo(
    () => !!pageName && !!platform && !submitting,
    [pageName, platform, submitting]
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ajouter une page</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Nom de la page"
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            size="small"
            required
          />
          <TextField
            label="Description"
            value={pageDesc}
            onChange={(e) => setPageDesc(e.target.value)}
            size="small"
          />
          <Autocomplete
            options={["web", "kiosk"]}
            value={platform}
            onChange={(_, val) => setPlatform(val || "web")}
            renderInput={(params) => <TextField {...params} label="Plateforme" size="small" />}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="Ajouter un champ (clé)"
              size="small"
              value={draftKey}
              onChange={(e) => setDraftKey(e.target.value)}
              error={!!draftKeyError}
              helperText={draftKeyError || "ex: header_title"}
            />
            <Button
              onClick={() => {
                if (draftKeyError) return;
                if (!texts.includes(draftKey)) {
                  setTexts((prev) => [...prev, draftKey]);
                  setDraftKey("");
                }
              }}
              startIcon={<AddIcon />}
              disabled={!!draftKeyError}
            >
              Ajouter
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {texts.map((t) => (
              <Chip key={t} label={t} onDelete={() => setTexts((prev) => prev.filter((x) => x !== t))} />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" variant="outlined" onClick={onClose}>
          Fermer
        </Button>
        <Button
          variant="contained"
          disabled={!canCreate}
          onClick={async () => {
            try {
              setSubmitting(true);
              await onCreate({
                page_name: pageName,
                platform,
                page_description: pageDesc,
                texts,
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          Créer la page
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(AddPageDialog);
